import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { io } from 'socket.io-client';
import { toast } from 'sonner';
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  Loader2,
  User,
  Maximize2,
  Minimize2,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import api from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SOCKET_PATH = '/api/socket.io';

// ICE servers for WebRTC (using public STUN servers)
const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' }
  ]
};

export const VideoConsultation = () => {
  const { roomId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Connection state
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [callStatus, setCallStatus] = useState('waiting'); // waiting, connecting, connected, ended
  const [error, setError] = useState(null);
  
  // Media state
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Peer info
  const [remotePeer, setRemotePeer] = useState(null);
  const [isInitiator, setIsInitiator] = useState(false);
  
  // Refs
  const socketRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const containerRef = useRef(null);
  const localStreamRef = useRef(null);  // Additional ref for stable stream access
  
  // Target user from URL params
  const targetUserId = searchParams.get('target');
  const targetUserName = searchParams.get('name') || 'User';

  // Initialize media stream
  const initializeMedia = useCallback(async () => {
    try {
      console.log('Requesting media access...');
      const constraints = {
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true
        }
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('Media stream obtained:', stream.getTracks().map(t => `${t.kind}: ${t.enabled}`));
      
      localStreamRef.current = stream;  // Store in ref for stable access
      setLocalStream(stream);
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
        // Ensure video plays
        try {
          await localVideoRef.current.play();
        } catch (playErr) {
          console.log('Autoplay handled by browser');
        }
      }
      return stream;
    } catch (err) {
      console.error('Error accessing media devices:', err);
      let errorMessage = 'Unable to access camera/microphone.';
      if (err.name === 'NotAllowedError') {
        errorMessage = 'Camera/microphone access denied. Please grant permissions in your browser settings.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No camera or microphone found on this device.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Camera/microphone is already in use by another application.';
      }
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    }
  }, []);

  // Create peer connection
  const createPeerConnection = useCallback((stream) => {
    const pc = new RTCPeerConnection(ICE_SERVERS);
    
    // Add local tracks to connection
    if (stream) {
      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
      });
    }
    
    // Handle incoming tracks
    pc.ontrack = (event) => {
      console.log('Received remote track');
      setRemoteStream(event.streams[0]);
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = event.streams[0];
      }
      setCallStatus('connected');
    };
    
    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate && socketRef.current?.connected) {
        socketRef.current.emit('ice_candidate', {
          target_sid: remotePeer?.sid,
          candidate: event.candidate
        });
      }
    };
    
    // Handle connection state changes
    pc.onconnectionstatechange = () => {
      console.log('Connection state:', pc.connectionState);
      if (pc.connectionState === 'connected') {
        setCallStatus('connected');
      } else if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        setCallStatus('ended');
        toast.error('Connection lost');
      }
    };
    
    peerConnectionRef.current = pc;
    return pc;
  }, [remotePeer?.sid]);

  // Initialize socket connection
  useEffect(() => {
    if (!roomId || !user) return;

    const socket = io(BACKEND_URL, {
      path: SOCKET_PATH,
      transports: ['websocket', 'polling']
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Socket connected');
      setConnectionStatus('connected');
      
      // Join the video room
      socket.emit('join_video_room', {
        room_id: roomId,
        user_id: user.user_id,
        user_name: user.full_name,
        user_type: user.user_type || 'user'
      });
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected');
      setConnectionStatus('disconnected');
    });

    socket.on('connection_status', (data) => {
      console.log('Connection status:', data);
    });

    socket.on('room_joined', async (data) => {
      console.log('Room joined:', data);
      setIsInitiator(data.is_initiator);
      
      // Initialize media
      const stream = await initializeMedia();
      if (!stream) return;
      
      // If there's already a participant, we're the caller
      if (data.participants.length > 0) {
        setRemotePeer(data.participants[0]);
        setCallStatus('connecting');
        
        // Create offer
        const pc = createPeerConnection(stream);
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        
        socket.emit('offer', {
          target_sid: data.participants[0].sid,
          sdp: pc.localDescription
        });
      } else {
        setCallStatus('waiting');
      }
    });

    socket.on('participant_joined', async (data) => {
      console.log('Participant joined:', data);
      setRemotePeer(data);
      toast.success(`${data.user_name} joined the call`);
    });

    socket.on('participant_left', (data) => {
      console.log('Participant left:', data);
      setRemotePeer(null);
      setRemoteStream(null);
      setCallStatus('ended');
      toast.info(`${data.user_name} left the call`);
    });

    socket.on('offer', async (data) => {
      console.log('Received offer from:', data.caller_name);
      setRemotePeer({
        sid: data.caller_sid,
        user_name: data.caller_name,
        user_id: data.caller_id
      });
      setCallStatus('connecting');
      
      // Get local stream if not already
      let stream = localStream;
      if (!stream) {
        stream = await initializeMedia();
        if (!stream) return;
      }
      
      // Create answer
      const pc = createPeerConnection(stream);
      await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      
      socket.emit('answer', {
        target_sid: data.caller_sid,
        sdp: pc.localDescription
      });
    });

    socket.on('answer', async (data) => {
      console.log('Received answer');
      if (peerConnectionRef.current) {
        await peerConnectionRef.current.setRemoteDescription(
          new RTCSessionDescription(data.sdp)
        );
      }
    });

    socket.on('ice_candidate', async (data) => {
      if (peerConnectionRef.current && data.candidate) {
        try {
          await peerConnectionRef.current.addIceCandidate(
            new RTCIceCandidate(data.candidate)
          );
        } catch (err) {
          console.error('Error adding ICE candidate:', err);
        }
      }
    });

    socket.on('peer_media_toggle', (data) => {
      toast.info(`${data.user_name} ${data.enabled ? 'enabled' : 'disabled'} their ${data.media_type}`);
    });

    socket.on('call_ended', (data) => {
      console.log('Call ended by:', data.ended_by);
      setCallStatus('ended');
      toast.info(`${data.ended_by} ended the call`);
    });

    socket.on('error', (data) => {
      console.error('Socket error:', data);
      setError(data.message);
      toast.error(data.message);
    });

    return () => {
      // Cleanup
      if (socket.connected) {
        socket.emit('leave_video_room', { room_id: roomId });
        socket.disconnect();
      }
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
      }
      // Use ref for cleanup to avoid stale closure
      const stream = localStreamRef.current;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [roomId, user, initializeMedia, createPeerConnection]);

  // Toggle audio - using ref for stable access
  const toggleAudio = useCallback(() => {
    const stream = localStreamRef.current || localStream;
    if (stream) {
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        const newState = !audioTrack.enabled;
        audioTrack.enabled = newState;
        setIsAudioEnabled(newState);
        console.log('Audio toggled:', newState);
        
        if (socketRef.current?.connected) {
          socketRef.current.emit('toggle_media', {
            media_type: 'audio',
            enabled: newState
          });
        }
      } else {
        console.error('No audio track found');
        toast.error('No audio track available');
      }
    } else {
      console.error('No local stream available for audio toggle');
      toast.error('Media not available');
    }
  }, [localStream]);

  // Toggle video - using ref for stable access
  const toggleVideo = useCallback(() => {
    const stream = localStreamRef.current || localStream;
    if (stream) {
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        const newState = !videoTrack.enabled;
        videoTrack.enabled = newState;
        setIsVideoEnabled(newState);
        console.log('Video toggled:', newState);
        
        if (socketRef.current?.connected) {
          socketRef.current.emit('toggle_media', {
            media_type: 'video',
            enabled: newState
          });
        }
      } else {
        console.error('No video track found');
        toast.error('No video track available');
      }
    } else {
      console.error('No local stream available for video toggle');
      toast.error('Media not available');
    }
  }, [localStream]);

  // End call
  const endCall = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('call_ended', { room_id: roomId });
      socketRef.current.emit('leave_video_room', { room_id: roomId });
    }
    
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }
    
    const stream = localStreamRef.current || localStream;
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    
    setCallStatus('ended');
    
    // Navigate back
    setTimeout(() => {
      navigate(-1);
    }, 1500);
  }, [roomId, localStream, navigate]);

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Render connection status
  const renderStatus = () => {
    switch (callStatus) {
      case 'waiting':
        return (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-10" style={{ background: 'rgba(0, 0, 0, 0.7)' }}>
            <Loader2 className="w-12 h-12 animate-spin mb-4" style={{ color: '#FFA500' }} />
            <p className="text-white text-lg">Waiting for {targetUserName} to join...</p>
            <p className="text-white/60 text-sm mt-2">Share this room link with your {user?.user_type === 'doctor' ? 'patient' : 'doctor'}</p>
          </div>
        );
      case 'connecting':
        return (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-10" style={{ background: 'rgba(0, 0, 0, 0.7)' }}>
            <Loader2 className="w-12 h-12 animate-spin mb-4" style={{ color: '#FFA500' }} />
            <p className="text-white text-lg">Connecting...</p>
          </div>
        );
      case 'ended':
        return (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-10" style={{ background: 'rgba(0, 0, 0, 0.9)' }}>
            <PhoneOff className="w-16 h-16 mb-4 text-red-500" />
            <p className="text-white text-xl">Call Ended</p>
            <p className="text-white/60 text-sm mt-2">Redirecting...</p>
          </div>
        );
      default:
        return null;
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center py-8">
        <div className="feature-card max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#EF4444' }} />
          <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#0A0A0A' }}>
            Connection Error
          </h2>
          <p className="mb-4" style={{ color: '#555555' }}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 rounded-full btn-primary-cta"
          >
            <RefreshCw className="w-5 h-5 inline mr-2" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="min-h-screen flex flex-col"
      style={{ background: '#1a1a1a' }}
      data-testid="video-consultation-page"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4" style={{ background: 'rgba(255, 165, 0, 0.1)' }}>
        <div className="flex items-center gap-3">
          <Video className="w-6 h-6" style={{ color: '#FFA500' }} />
          <div>
            <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Video Consultation
            </h1>
            <p className="text-sm text-white/60">
              {callStatus === 'connected' ? `Connected with ${remotePeer?.user_name || targetUserName}` : 'Establishing connection...'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            connectionStatus === 'connected' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
          }`}>
            {connectionStatus === 'connected' ? 'Connected' : 'Connecting...'}
          </span>
        </div>
      </div>

      {/* Video Container */}
      <div className="flex-1 relative">
        {renderStatus()}
        
        {/* Remote Video (Main) */}
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          {remoteStream ? (
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
              data-testid="remote-video"
            />
          ) : (
            <div className="flex flex-col items-center justify-center">
              <div className="w-32 h-32 rounded-full flex items-center justify-center mb-4" style={{ background: 'rgba(255, 165, 0, 0.15)' }}>
                <User className="w-16 h-16" style={{ color: '#FFA500' }} />
              </div>
              <p className="text-white/60">{remotePeer?.user_name || targetUserName}</p>
            </div>
          )}
        </div>

        {/* Local Video (Picture-in-Picture) */}
        <div 
          className="absolute bottom-24 right-4 w-48 h-36 rounded-xl overflow-hidden shadow-2xl border-2"
          style={{ borderColor: '#FFA500' }}
        >
          {localStream && isVideoEnabled ? (
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              data-testid="local-video"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ background: '#2a2a2a' }}>
              <VideoOff className="w-8 h-8 text-white/40" />
            </div>
          )}
          <div className="absolute bottom-2 left-2 px-2 py-1 rounded text-xs text-white" style={{ background: 'rgba(0,0,0,0.6)' }}>
            You
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="p-6 flex items-center justify-center gap-4" style={{ background: 'rgba(0, 0, 0, 0.8)' }}>
        <button
          onClick={toggleAudio}
          className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
            isAudioEnabled ? 'bg-white/10 hover:bg-white/20' : 'bg-red-500 hover:bg-red-600'
          }`}
          title={isAudioEnabled ? 'Mute' : 'Unmute'}
          data-testid="toggle-audio-btn"
        >
          {isAudioEnabled ? (
            <Mic className="w-6 h-6 text-white" />
          ) : (
            <MicOff className="w-6 h-6 text-white" />
          )}
        </button>

        <button
          onClick={toggleVideo}
          className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
            isVideoEnabled ? 'bg-white/10 hover:bg-white/20' : 'bg-red-500 hover:bg-red-600'
          }`}
          title={isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
          data-testid="toggle-video-btn"
        >
          {isVideoEnabled ? (
            <Video className="w-6 h-6 text-white" />
          ) : (
            <VideoOff className="w-6 h-6 text-white" />
          )}
        </button>

        <button
          onClick={endCall}
          className="w-16 h-16 rounded-full flex items-center justify-center bg-red-500 hover:bg-red-600 transition-all"
          title="End call"
          data-testid="end-call-btn"
        >
          <PhoneOff className="w-7 h-7 text-white" />
        </button>

        <button
          onClick={toggleFullscreen}
          className="w-14 h-14 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 transition-all"
          title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          data-testid="toggle-fullscreen-btn"
        >
          {isFullscreen ? (
            <Minimize2 className="w-6 h-6 text-white" />
          ) : (
            <Maximize2 className="w-6 h-6 text-white" />
          )}
        </button>
      </div>
    </div>
  );
};

export default VideoConsultation;
